import { type ApolloError, useMutation } from '@apollo/client';
import { useConfirmationDialog } from '@openx/components-core/lib/Dialog/ConfirmationDialog/useConfirmationDialog';
import { useSnackbar } from 'notistack';
import { useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../../components/AuthContext/context';
import { ActivateAudienceDocument } from '../../../../graphql/mutation/audiences/ActivateAudience.generated';
import { DuplicateAudienceDocument } from '../../../../graphql/mutation/audiences/DuplicateAudience.generated';
import { CreateLogDocument } from '../../../../graphql/mutation/error/createLog.generated';
import type { GetAudiencesListQuery } from '../../../../graphql/query/audiences/GetAudiencesList.generated';
import { type ExtendedGQLError, NestedErrorCode } from '../../../graphql/types';
import { useLocalErrorHandling } from '../../../graphql/useLocalErrorHandling';
import {
  DuplicateAudienceDialogContent,
  type DuplicateAudienceDialogErrorSetter,
} from '../components/DuplicateAudienceDialogContent';

type OpenDuplicateModalParams = {
  audience: Pick<GetAudiencesListQuery['audience'][number], 'id' | 'name'> & {
    account?: Pick<NonNullable<GetAudiencesListQuery['audience'][number]['account']>, 'name'> | null;
  };
};

const audienceNameUniquenessMessage = /audience_account_id_name_key/;

const isAudienceNameConflict = (error: ApolloError): boolean => {
  const nestedConflict = error.graphQLErrors?.some(err => {
    const extendedErr = err as ExtendedGQLError;
    return extendedErr.extensions?.errors?.some(
      nestedErr => nestedErr.code === NestedErrorCode.JOINT_UNIQUENESS_VIOLATION && nestedErr.path?.endsWith('.name')
    );
  });

  if (nestedConflict) {
    return true;
  }

  return error.graphQLErrors?.some(err => audienceNameUniquenessMessage.test(err.message ?? ''));
};

export const useDuplicateAudience = (onSuccess?: () => void) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [pushLog] = useMutation(CreateLogDocument);
  const { currentUser, session } = useAuth();
  const [duplicateAudience, { loading: duplicateLoading }] = useMutation(DuplicateAudienceDocument);
  const errorSetterRef = useRef<DuplicateAudienceDialogErrorSetter | null>(null);

  useLocalErrorHandling(
    DuplicateAudienceDocument,
    useCallback(
      error => {
        if (error.code !== NestedErrorCode.JOINT_UNIQUENESS_VIOLATION || !error.path.endsWith('.name')) return false;
        enqueueSnackbar(t('Audience with such name already exists'), { variant: 'error' });
        return true;
      },
      [enqueueSnackbar, t]
    )
  );
  const [activateAudience] = useMutation(ActivateAudienceDocument, {
    refetchQueries: ['GetAudiencesList'],
  });
  const {
    open: dialogOpen,
    close: dialogClose,
    setLoading,
    toogleConfirmationDisabled,
  } = useConfirmationDialog({
    confirmationDisabled: true,
  });
  const nameRef = useRef<string>('');

  const duplicate = useCallback(
    async (audience: OpenDuplicateModalParams['audience']) => {
      try {
        const response = await duplicateAudience({
          variables: {
            id: audience.id,
            name: nameRef.current,
            user_id: session?.id ?? '',
          },
        });

        const clonedAudienceId = response?.data?.cloneAudience?.id;

        if (!clonedAudienceId) {
          throw new Error('Missing cloned audience id');
        }

        await activateAudience({ variables: { id: clonedAudienceId } });

        enqueueSnackbar(t('Audience successfully duplicated'), { variant: 'success' });

        return { ok: true as const };
      } catch (error) {
        const location = window.location.href;
        const apolloError = error as ApolloError;
        const { message } = apolloError;
        const nameConflict = !!apolloError.graphQLErrors && isAudienceNameConflict(apolloError);

        await pushLog({
          variables: {
            location: location,
            msg: message || 'Failed to duplicate audience',
            name: currentUser?.displayName,
            email: currentUser?.email,
          },
        });

        if (nameConflict) {
          errorSetterRef.current?.(t('Audience with such name already exists'));
          toogleConfirmationDisabled(true);
          return { ok: false as const, nameConflict: true as const };
        }

        if (message !== 'Joint uniqueness violation') {
          enqueueSnackbar(t('Failed to duplicate audience'), {
            variant: 'error',
          });
        }

        return { ok: false as const, nameConflict: false as const };
      }
    },
    [enqueueSnackbar, pushLog, t, currentUser, activateAudience, duplicateAudience, session, toogleConfirmationDisabled]
  );

  const openDuplicateModal = useCallback(
    ({ audience }: OpenDuplicateModalParams) => {
      const onConfirmed = async () => {
        try {
          setLoading(true);
          const result = await duplicate(audience);

          if (result.ok) {
            onSuccess?.();
            dialogClose();
          }
        } finally {
          setLoading(false);
        }
      };

      dialogOpen({
        confirmationButtonColor: 'primary',
        confirmationLabel: t('Duplicate'),
        content: (
          <DuplicateAudienceDialogContent
            audience={audience}
            nameRef={nameRef}
            toogleConfirmationDisabled={toogleConfirmationDisabled}
            errorSetterRef={errorSetterRef}
          />
        ),
        customContent: true,
        hideConfirm: !audience,
        onConfirmed,
        showExtraBtn: false,
        title: t('Duplicate Audience'),
      });
    },
    [dialogOpen, dialogClose, setLoading, toogleConfirmationDisabled, t, duplicate, onSuccess]
  );

  return { openDuplicateModal, isLoading: duplicateLoading };
};
